import { fillFields, type ApplyContext } from './apply.js';

const GENERATED = 'data-siteflow-generated';
const TEMPLATE = 'data-siteflow-template';

/**
 * Element templates are promoted into a detached `<template>` on first render.
 * The cache is keyed by container so a `refresh()` does not have to find (and
 * cannot accidentally re-promote) the markup a second time.
 */
const templates = new WeakMap<Element, HTMLTemplateElement>();

function resolveTemplate(container: Element): HTMLTemplateElement | null {
  const cached = templates.get(container);
  if (cached) return cached;

  const declared = container.querySelector(
    `:scope > template[${TEMPLATE}]`,
  ) as HTMLTemplateElement | null;
  if (declared) {
    templates.set(container, declared);
    return declared;
  }

  // Fallback: the author marked a real element as the template so the page still
  // looks right with JavaScript disabled. Move it out of the document once.
  const sample = Array.from(container.children).find((child) => child.hasAttribute(TEMPLATE));
  if (!sample) return null;

  const template = container.ownerDocument.createElement('template');
  sample.removeAttribute(TEMPLATE);
  template.content.appendChild(sample);
  templates.set(container, template);
  return template;
}

export type RepeaterOutcome = 'applied' | 'skipped';

/**
 * Renders a repeater container from an array of items.
 *
 * An absent key leaves the hand-authored markup alone; an empty array clears the
 * generated rows and flags the container so CSS can show an empty state.
 */
export function renderRepeater(
  container: Element,
  items: unknown,
  ctx: ApplyContext,
): RepeaterOutcome {
  if (items === undefined || items === null) return 'skipped';
  if (!Array.isArray(items)) return 'skipped';

  const template = resolveTemplate(container);
  if (!template) return 'skipped';

  for (const previous of Array.from(container.querySelectorAll(`:scope > [${GENERATED}]`))) {
    previous.remove();
  }

  if (items.length === 0) {
    container.setAttribute('data-siteflow-empty', '');
    container.setAttribute('data-siteflow-state', 'applied');
    return 'applied';
  }
  container.removeAttribute('data-siteflow-empty');

  const doc = container.ownerDocument;
  const batch = doc.createDocumentFragment();

  items.forEach((item, index) => {
    const clone = template.content.cloneNode(true) as DocumentFragment;
    for (const child of Array.from(clone.children)) {
      child.setAttribute('data-siteflow-index', String(index));
      child.setAttribute(GENERATED, '');
    }
    fillFields(clone, item, ctx);
    batch.appendChild(clone);
  });

  container.appendChild(batch);
  container.setAttribute('data-siteflow-state', 'applied');
  return 'applied';
}
